// src/pages/RFQCreate.js
import React, { useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import AppNavbar from "../components/AppNavbar";
import Footer from "../components/Footer";

const RFQForm = () => {
  const [form, setForm] = useState({
    product_name: "",
    quantity: "",
    customer_name: "",
    customer_email: "",
    status: "Pending",
    notes: "",
  });

  const [otherProduct, setOtherProduct] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState({ type: "", message: "" });
  const [apiErrors, setApiErrors] = useState([]);
  const [touched, setTouched] = useState({});

  const PRODUCT_OPTIONS = [
    { value: "", label: "Select a product..." },
    { value: "Contactless Water Dispenser", label: "Contactless Water Dispenser" },
    { value: "Smart Energy Monitoring Devices", label: "Smart Energy Monitoring Devices" },
    { value: "Smart Facility Monitoring System", label: "Smart Facility Monitoring System" },
    { value: "IoT Automation Gateways", label: "IoT Automation Gateways" },
    { value: "Others", label: "Others" },
  ];

  // IMPORTANT: must match PHP allowed list to avoid 422
  const STATUS_OPTIONS = ["Pending", "Quoted", "Closed", "Cancelled"];

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (name === "product_name" && value !== "Others") {
      setOtherProduct("");
      setTouched((t) => ({ ...t, otherProduct: false }));
    }
  };

  const onBlur = (e) => {
    const { name } = e.target;
    setTouched((t) => ({ ...t, [name]: true }));
  };

  const validate = () => {
    const errors = {};
    if (!form.product_name) errors.product_name = "Please choose a product.";
    if (form.product_name === "Others" && !otherProduct.trim()) {
      errors.otherProduct = "Please specify the product.";
    }
    if (!form.quantity || Number(form.quantity) <= 0) errors.quantity = "Quantity must be greater than 0.";
    if (!form.customer_name.trim()) errors.customer_name = "Customer name is required.";

    // ✅ Email is now REQUIRED
    const email = (form.customer_email || "").trim();
    if (!email) {
      errors.customer_email = "Customer email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.customer_email = "Customer email is invalid.";
    }

    if (!STATUS_OPTIONS.includes(form.status)) errors.status = "Invalid status.";
    return errors;
  };

  const errors = validate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({
      product_name: true,
      otherProduct: true,
      quantity: true,
      customer_name: true,
      customer_email: true,
      status: true,
      notes: true,
    });
    setApiErrors([]);
    setAlert({ type: "", message: "" });

    if (Object.keys(errors).length) {
      setAlert({ type: "danger", message: "Please fix the errors below." });
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        ...form,
        customer_email: form.customer_email.trim(), // normalize
        product_name:
          form.product_name === "Others" && otherProduct.trim()
            ? `Others: ${otherProduct.trim()}`
            : form.product_name,
        quantity: Number(form.quantity || 0),
      };

      const response = await fetch("https://vionstech.com/apis/rfq_create.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      let result = {};
      try {
        result = await response.json();
      } catch {
        // non-JSON response
      }

      if (response.status === 422) {
        const errs = Array.isArray(result?.errors) ? result.errors : ["Validation error"];
        setApiErrors(errs);
        setAlert({ type: "danger", message: "Please fix the errors below." });
        return;
      }

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Failed to create RFQ.");
      }

      setAlert({
        type: "success",
        message: `RFQ created successfully (ID: ${result.rfq_id}).`,
      });
      resetForm();
    } catch (err) {
      setAlert({
        type: "danger",
        message: err?.message || "Failed to create RFQ.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({
      product_name: "",
      quantity: "",
      customer_name: "",
      customer_email: "",
      status: "Pending",
      notes: "",
    });
    setOtherProduct("");
    setTouched({});
    setApiErrors([]);
  };

  return (
    <>
      <AppNavbar />
      <main>
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-12 col-lg-10 col-xl-8">
              <div className="card shadow-sm border-0 overflow-hidden">
                <div className="card-body p-4 p-md-5" style={{ backgroundColor: "var(--card)" }}>
                  {alert.message && (
                    <div
                      className={`alert alert-${alert.type} alert-dismissible fade show rounded-3 mb-4`}
                      role="alert"
                      style={{
                        backgroundColor: alert.type === "success" ? "var(--success)" : "var(--error)",
                        color: "white",
                        border: "none",
                      }}
                    >
                      {alert.message}
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setAlert({ type: "", message: "" })}
                        aria-label="Close"
                      ></button>
                    </div>
                  )}

                  {/* Server-side validation errors (422) */}
                  {Array.isArray(apiErrors) && apiErrors.length > 0 && (
                    <div className="alert alert-danger rounded-3" role="alert" style={{ border: "none" }}>
                      <ul className="mb-0 ps-3">
                        {apiErrors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} noValidate className="needs-validation">
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="product_name" className="form-label fw-medium" style={{ color: "var(--secondarytext)" }}>
                          Product
                        </label>
                        <select
                          className={`form-select ${touched.product_name && errors.product_name ? "is-invalid" : ""}`}
                          id="product_name"
                          name="product_name"
                          value={form.product_name}
                          onChange={onChange}
                          onBlur={onBlur}
                          required
                          style={{ borderColor: "var(--cardborder)", backgroundColor: "var(--card)" }}
                        >
                          {PRODUCT_OPTIONS.map((opt) => (
                            <option key={opt.value || "blank"} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        {touched.product_name && errors.product_name && (
                          <div className="invalid-feedback d-flex align-items-center">
                            <i className="fas fa-exclamation-circle me-2"></i>
                            {errors.product_name}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label htmlFor="quantity" className="form-label fw-medium" style={{ color: "var(--secondarytext)" }}>
                          Quantity
                        </label>
                        <input
                          type="number"
                          className={`form-control ${touched.quantity && errors.quantity ? "is-invalid" : ""}`}
                          id="quantity"
                          name="quantity"
                          min="1"
                          value={form.quantity}
                          onChange={onChange}
                          onBlur={onBlur}
                          placeholder="e.g. 10"
                          required
                          style={{ borderColor: "var(--cardborder)", backgroundColor: "var(--card)" }}
                        />
                        {touched.quantity && errors.quantity && (
                          <div className="invalid-feedback d-flex align-items-center">
                            <i className="fas fa-exclamation-circle me-2"></i>
                            {errors.quantity}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* "Others" product detail */}
                    {form.product_name === "Others" && (
                      <div className="mb-3">
                        <label htmlFor="otherProduct" className="form-label fw-medium" style={{ color: "var(--secondarytext)" }}>
                          Specify Product
                        </label>
                        <input
                          type="text"
                          className={`form-control ${touched.otherProduct && errors.otherProduct ? "is-invalid" : ""}`}
                          id="otherProduct"
                          name="otherProduct"
                          value={otherProduct}
                          onChange={(e) => setOtherProduct(e.target.value)}
                          onBlur={() => setTouched((t) => ({ ...t, otherProduct: true }))}
                          placeholder="Describe the product you need..."
                          required
                          style={{ borderColor: "var(--cardborder)", backgroundColor: "var(--card)" }}
                        />
                        {touched.otherProduct && errors.otherProduct && (
                          <div className="invalid-feedback d-flex align-items-center">
                            <i className="fas fa-exclamation-circle me-2"></i>
                            {errors.otherProduct}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="customer_name" className="form-label fw-medium" style={{ color: "var(--secondarytext)" }}>
                          Customer Name
                        </label>
                        <input
                          type="text"
                          className={`form-control ${touched.customer_name && errors.customer_name ? "is-invalid" : ""}`}
                          id="customer_name"
                          name="customer_name"
                          value={form.customer_name}
                          onChange={onChange}
                          onBlur={onBlur}
                          placeholder="ABC Health Pvt Ltd"
                          required
                          style={{ borderColor: "var(--cardborder)", backgroundColor: "var(--card)" }}
                        />
                        {touched.customer_name && errors.customer_name && (
                          <div className="invalid-feedback d-flex align-items-center">
                            <i className="fas fa-exclamation-circle me-2"></i>
                            {errors.customer_name}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label
                          htmlFor="customer_email"
                          className="form-label fw-medium"
                          style={{ color: "var(--secondarytext)" }}
                        >
                          Customer Email <span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          required
                          className={`form-control ${touched.customer_email && errors.customer_email ? "is-invalid" : ""}`}
                          id="customer_email"
                          name="customer_email"
                          value={form.customer_email}
                          onChange={onChange}
                          onBlur={onBlur}
                          placeholder="contact@company.com"
                          style={{ borderColor: "var(--cardborder)", backgroundColor: "var(--card)" }}
                        />
                        {touched.customer_email && errors.customer_email && (
                          <div className="invalid-feedback d-flex align-items-center">
                            <i className="fas fa-exclamation-circle me-2"></i>
                            {errors.customer_email}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="row d-none ">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="status" className="form-label fw-medium" style={{ color: "var(--secondarytext)" }}>
                          Status
                        </label>
                        <select
                          className={`form-select ${touched.status && errors.status ? "is-invalid" : ""}`}
                          id="status"
                          name="status"
                          value={form.status}
                          onChange={onChange}
                          onBlur={onBlur}
                          style={{ borderColor: "var(--cardborder)", backgroundColor: "var(--card)" }}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                        {touched.status && errors.status && (
                          <div className="invalid-feedback d-flex align-items-center">
                            <i className="fas fa-exclamation-circle me-2"></i>
                            {errors.status}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mb-4">
                      <label htmlFor="notes" className="form-label fw-medium" style={{ color: "var(--secondarytext)" }}>
                        Notes
                      </label>
                      <textarea
                        className="form-control"
                        id="notes"
                        name="notes"
                        value={form.notes}
                        onChange={onChange}
                        rows="4"
                        placeholder="Any special requirements, delivery preferences, or additional information..."
                        style={{ borderColor: "var(--cardborder)", backgroundColor: "var(--card)" }}
                      />
                    </div>

                    <div className="d-flex gap-3 flex-wrap">
                      <button
                        type="submit"
                        className="btn px-4 py-2 fw-medium"
                        disabled={submitting}
                        style={{ backgroundColor: "var(--primary)", borderColor: "var(--primary)", color: "white", minWidth: "140px" }}
                      >
                        {submitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Submitting...
                          </>
                        ) : (
                          <>Submit</>
                        )}
                      </button>

                      <button
                        type="button"
                        className="btn btn-outline-secondary px-4 py-2 fw-medium"
                        onClick={resetForm}
                        disabled={submitting}
                        style={{ borderColor: "var(--cardborder)", color: "var(--secondarytext)" }}
                      >
                        <i className="fas fa-redo me-2"></i>
                        Reset Form
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          <style>{`
            :root {
              --primary: #013EFB;
              --primary-light: #CCD6FF;
              --primary-dark: #001EB3;
              --canvas: #F8F9FC;
              --card: #FFFFFF;
              --cardborder: #E2E6ED;
              --secondary: #5A8CBA;
              --secondarytext: #1A2233;
              --accent: #FF9E1C;
              --chip: #E6EDFF;
              --chiptext: #001EB3;
              --chipalt: #F0F5FF;
              --success: #0CA789;
              --error: #F25A5A;
            }

            main { padding: 6rem 0; }
            .card { border-radius: 12px; overflow: hidden; }

            .form-control, .form-select {
              border-radius: 8px;
              padding: 10px 14px;
              transition: all 0.2s ease;
            }

            .form-control:focus, .form-select:focus {
              border-color: var(--primary);
              box-shadow: 0 0 0 0.2rem var(--primary-light);
            }

            .btn { border-radius: 8px; transition: all 0.2s ease; }
            .btn:hover { transform: translateY(-2px); }
            .btn:disabled { opacity: 0.7; }

            .invalid-feedback { color: var(--error); font-size: 0.875rem; }

            @media (max-width: 768px) {
              .card-body { padding: 1.5rem; }
            }
          `}</style>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default RFQForm;
